import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { useToast } from '../../notifications';
import {
  activateReminderDefinition,
  backfillReminders,
  listReminderChannels,
  listReminderDefinitions,
} from '../services/reminderEngineService';
import type { ReminderDefinitionStatus, ReminderEngineChannelInfo, ReminderEngineDefinition } from '../types/ReminderEngine';
import { formatRuleCriteriaSummary, formatRuleTimingSummary } from '../utils/definitionEditorForm';
import { getReminderStatusClass } from '../utils/reminderEngineStatus';
import sharedStyles from './reminderEngineShared.module.css';

export const ReminderDefinitionsPage: React.FC = () => {
  const { t } = useTranslation(['reminderEngine', 'common']);
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PermissionScopes.remindersManage);

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [channels, setChannels] = React.useState<ReminderEngineChannelInfo[]>([]);
  const [definitions, setDefinitions] = React.useState<ReminderEngineDefinition[]>([]);
  const [statusFilter, setStatusFilter] = React.useState<ReminderDefinitionStatus | ''>('');

  const [backfillBusy, setBackfillBusy] = React.useState(false);
  const [includeTasks, setIncludeTasks] = React.useState(true);
  const [includeAppointments, setIncludeAppointments] = React.useState(true);
  const [batchSize, setBatchSize] = React.useState(200);

  const loadData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [channelItems, definitionItems] = await Promise.all([
        listReminderChannels(),
        listReminderDefinitions({ status: statusFilter || undefined }),
      ]);
      setChannels(channelItems);
      setDefinitions(definitionItems);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  React.useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleActivate = async (definitionId: string) => {
    try {
      await activateReminderDefinition(definitionId);
      showToast(t('definitionActivated'), 'success');
      await loadData();
    } catch (err) {
      showToast(err instanceof Error ? err.message : t('actionFailed'), 'error');
    }
  };

  const handleBackfill = async () => {
    setBackfillBusy(true);
    try {
      const result = await backfillReminders({ includeTasks, includeAppointments, batchSize });
      showToast(
        t('backfillComplete', {
          tasks: result.tasksProcessed,
          appointments: result.appointmentsProcessed,
          errors: result.errors,
        }),
        result.errors > 0 ? 'warning' : 'success',
      );
      await loadData();
    } catch (err) {
      showToast(err instanceof Error ? err.message : t('actionFailed'), 'error');
    } finally {
      setBackfillBusy(false);
    }
  };

  return (
    <>
      <section className={sharedStyles.sectionCard}>
        <h3 className={sharedStyles.sectionTitle}>{t('channelsTitle')}</h3>
        <p className={sharedStyles.emptyState}>{t('channelsHelp')}</p>
        <div className={sharedStyles.chipRow}>
          {channels.length === 0 ? (
            <span className={sharedStyles.emptyState}>{t('noChannels')}</span>
          ) : (
            channels.map((channel) => (
              <span key={channel.key} className={sharedStyles.chip} title={channel.senderType}>
                {channel.key}
              </span>
            ))
          )}
        </div>
      </section>

      {canManage && (
        <section className={sharedStyles.sectionCard}>
          <h3 className={sharedStyles.sectionTitle}>{t('backfillTitle')}</h3>
          <p className={sharedStyles.sectionIntro}>{t('backfillSubtitle')}</p>
          <div className={sharedStyles.backfillForm}>
            <div className={sharedStyles.backfillOptions}>
              <label className={sharedStyles.backfillCheckbox}>
                <input type="checkbox" checked={includeTasks} onChange={(event) => setIncludeTasks(event.target.checked)} disabled={backfillBusy} />
                <span>{t('includeTasks')}</span>
              </label>
              <label className={sharedStyles.backfillCheckbox}>
                <input
                  type="checkbox"
                  checked={includeAppointments}
                  onChange={(event) => setIncludeAppointments(event.target.checked)}
                  disabled={backfillBusy}
                />
                <span>{t('includeAppointments')}</span>
              </label>
            </div>
            <label className={sharedStyles.backfillField}>
              <span>{t('batchSize')}</span>
              <input
                className="text-input"
                type="number"
                min={1}
                max={1000}
                value={batchSize}
                onChange={(event) => setBatchSize(Number(event.target.value))}
                disabled={backfillBusy}
              />
            </label>
            <div className={sharedStyles.backfillActions}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void handleBackfill()}
                disabled={backfillBusy || (!includeTasks && !includeAppointments)}
              >
                {backfillBusy ? t('runningBackfill') : t('runBackfill')}
              </button>
            </div>
          </div>
        </section>
      )}

      <section className={sharedStyles.sectionCard}>
        <div className={sharedStyles.headerRow}>
          <h3 className={sharedStyles.sectionTitle}>{t('definitionsTitle')}</h3>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => void loadData()} disabled={loading}>
            {t('refresh', { ns: 'common' })}
          </button>
        </div>

        <div className={sharedStyles.filters}>
          <label>
            {t('status')}
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as ReminderDefinitionStatus | '')}>
              <option value="">{t('all', { ns: 'common' })}</option>
              <option value="active">active</option>
              <option value="draft">draft</option>
              <option value="archived">archived</option>
            </select>
          </label>
        </div>

        {loading && <Spinner />}
        {error && <p className={sharedStyles.error}>{error}</p>}

        {!loading && !error && definitions.length === 0 && (
          <p className={sharedStyles.emptyState}>{t('noDefinitions')}</p>
        )}

        {!loading && !error && definitions.length > 0 && (
          <table className={sharedStyles.table}>
            <thead>
              <tr>
                <th>{t('name')}</th>
                <th>{t('category')}</th>
                <th>{t('version')}</th>
                <th>{t('status')}</th>
                <th>{t('rules')}</th>
                <th>{t('actions', { ns: 'common' })}</th>
              </tr>
            </thead>
            <tbody>
              {definitions.map((definition) => (
                <tr key={definition.id}>
                  <td>
                    <div className={sharedStyles.definitionNameCell}>
                      <strong>{definition.name}</strong>
                      <code className={sharedStyles.definitionKey}>{definition.key}</code>
                    </div>
                  </td>
                  <td>{definition.category || '—'}</td>
                  <td>{definition.version}</td>
                  <td><span className={getReminderStatusClass(definition.status)}>{definition.status}</span></td>
                  <td>
                    <ul className={sharedStyles.rulePreviewList}>
                      {definition.rules.slice(0, 3).map((rule) => (
                        <li key={rule.id}>
                          {rule.name} · {formatRuleTimingSummary(rule.timing)}
                          {rule.criteria.length > 0 ? ` · ${formatRuleCriteriaSummary(rule.criteria)}` : ''}
                        </li>
                      ))}
                      {definition.rules.length > 3 && (
                        <li>{t('moreRules', { count: definition.rules.length - 3 })}</li>
                      )}
                    </ul>
                  </td>
                  <td className={sharedStyles.actionsCell}>
                    {canManage && (
                      <Link to={`/reminder-engine/definitions/${encodeURIComponent(definition.key)}/edit`} className="btn btn-secondary btn-sm">
                        {t('edit')}
                      </Link>
                    )}
                    <Link
                      to={`/reminder-engine/scheduled?definitionKey=${encodeURIComponent(definition.key)}`}
                      className="btn btn-secondary btn-sm"
                    >
                      {t('viewScheduled')}
                    </Link>
                    {canManage && definition.status !== 'active' && (
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => void handleActivate(definition.id)}>
                        {t('activate')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
};
